import React, { useCallback, useMemo, useRef, useState } from "react";
import { Control, Controller, FieldValues } from "react-hook-form";
import { PatternFormat } from "react-number-format";

import { InputLabelHeight } from "@covertree/types";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import InfoButton from "@covertree/ui/buttons/InfoButton";
import { getInputLabelHeight } from "@covertree/utils";
import { useMediaQuery } from "@mui/material";

type Props<TValue extends FieldValues> = Omit<TextFieldProps, "variant"> & {
  id: string;
  name: string; // @TODO investigate why "Path" from "react-hook-form" type is not working
  control: Control<TValue>;
  label: React.ReactNode;
  labelHeight?: InputLabelHeight;
  dataTestId?: string;
  type?: React.InputHTMLAttributes<unknown>["type"];
  error?: React.ReactNode;
  tooltip?: string;
  optional?: boolean;
  disabled?: boolean;
};

interface NumberFormatCustomProps {
  inputRef: (instance: HTMLInputElement | null) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

export const FormPhoneInput = ({
  inputRef,
  onChange,
  ...other
}: NumberFormatCustomProps) => (
  <PatternFormat
    {...other}
    getInputRef={inputRef}
    onValueChange={(values) => {
      onChange({
        target: {
          name: other.name,
          value: values.value,
        },
      });
    }}
    valueIsNumericString
    format="(###) ###-####"
    type="tel"
  />
);

const FormPhoneField = <TValue extends FieldValues>({
  id,
  name,
  control,
  label,
  labelHeight = InputLabelHeight.DEFAULT,
  dataTestId,
  tooltip,
  optional,
  disabled = false,
  ...textFieldProps
}: Props<TValue>): JSX.Element => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const labelComponentHeight = useMemo(
    () => getInputLabelHeight(theme, labelHeight),
    [labelHeight, theme]
  );

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const labelRef = useRef<HTMLParagraphElement>(null);

  const handleTooltipClose = useCallback(() => setTooltipOpen(false), []);

  const handleHelpIconClick = useCallback(() => {
    setTooltipOpen((val) => !val);
  }, []);

  return (
    <Controller
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      name={name as any}
      control={control}
      render={({ field, fieldState: { error, invalid } }) => (
        <FormControl variant="standard" fullWidth error={invalid}>
          <InputLabel
            htmlFor={id}
            id={`${id}-label`}
            sx={{
              position: "static",
              alignItems: "flex-end",
              display: "flex",
              height: isDesktop ? labelComponentHeight : "auto",
              transform: "none",
              "& > p": {
                display: "flex",
                alignItems: "center",
                flex: 1,
                width: "100%",
                whiteSpace: "break-spaces",
                my: 0,
              },
            }}>
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <Tooltip
                onClose={handleTooltipClose}
                open={tooltipOpen}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                PopperProps={{
                  style: { width: labelRef.current?.clientWidth },
                }}
                title={tooltip ?? ""}
                sx={{
                  margin: theme.spacing(0.75, 0),
                }}>
                <Typography variant="body2" ref={labelRef}>
                  {label}
                  {tooltip && (
                    <InfoButton
                      data-testid={`${dataTestId}-tooltip`}
                      onClick={handleHelpIconClick}
                      sx={{
                        marginLeft: theme.spacing(),
                      }}
                    />
                  )}
                </Typography>
              </Tooltip>
            </ClickAwayListener>
          </InputLabel>

          <TextField
            id={id}
            error={!!error}
            disabled={disabled}
            variant="outlined"
            InputProps={{
              endAdornment: optional ? (
                <InputAdornment position="end">
                  <Typography variant="subtitle1">Optional</Typography>
                </InputAdornment>
              ) : undefined,
              inputProps: { "data-testid": dataTestId },
              inputComponent: FormPhoneInput as never,
            }}
            {...textFieldProps}
            {...field}
          />
          <FormHelperText error {...(!error?.message && { sx: { margin: 0 } })}>
            {error?.message}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export default FormPhoneField;
